/* qp-about.js — About page behaviour (spec
   docs/superpowers/specs/2026-09-29-about-page-rework-design.md §3-§5).
   DISPLAY-ONLY: touches no form, no cart, no line-item property.

   1. <qp-film-band> — poster facade. Click on the play control hides the
      poster, reveals the Shopify-hosted <video> and plays with sound.
      Exposes seekTo(seconds): reveal -> seek -> play. Nothing autoplays.
   2. Process tiles — any [data-qp-film-seek] click calls seekTo on the
      page's film band, then scrolls the band into view. Without a film
      that has a video the process section is marked
      data-qp-film-linked="false" and clicks are inert.
   3. Support rail — scroll-spy sets aria-current="true" on the rail
      entry whose target is at the top of the viewport (under the sticky
      header); on the mobile segmented control the active chip is kept
      in view. Deep links (#faq-…) open the matching <details>.

   Loaded (defer) by three sections; the boot flag below makes the second
   and third loads no-ops. Theme editor: shopify:section:load re-inits. */
(function () {
  'use strict';

  if (window.__qpAboutBooted) return;
  window.__qpAboutBooted = true;

  var reducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function headerOffset() {
    var raw = getComputedStyle(document.documentElement).getPropertyValue('--m-header-height');
    return parseFloat(raw) || 0;
  }

  /* ── 1 · <qp-film-band> ─────────────────────────────────────────────── */

  if (window.customElements && !customElements.get('qp-film-band')) {
    customElements.define(
      'qp-film-band',
      class QpFilmBand extends HTMLElement {
        connectedCallback() {
          if (this.__qpFilmBound) return;
          this.__qpFilmBound = true;
          var self = this;
          var play = this.querySelector('[data-qp-film-play]');
          if (play) {
            play.addEventListener('click', function () {
              if (!self.reveal()) return;
              self.play();
              // The button leaves the DOM with the poster — hand focus to the player.
              var v = self.video;
              if (v) v.focus({ preventScroll: true });
            });
          }
        }

        get video() {
          return this.querySelector('video');
        }

        get hasVideo() {
          return this.getAttribute('data-has-video') === 'true' && !!this.video;
        }

        reveal() {
          if (!this.hasVideo) return false;
          var poster = this.querySelector('[data-qp-film-poster]');
          var player = this.querySelector('[data-qp-film-player]');
          if (poster) poster.setAttribute('hidden', '');
          if (player) player.removeAttribute('hidden');
          this.setAttribute('data-revealed', 'true');
          return true;
        }

        play() {
          var v = this.video;
          if (!v) return;
          var p = v.play();
          if (p && typeof p.catch === 'function') p.catch(function () {});
        }

        seekTo(seconds) {
          var v = this.video;
          if (!v || !this.reveal()) return false;
          var target = Math.max(0, parseFloat(seconds) || 0);
          var self = this;
          function apply() {
            try {
              v.currentTime = target;
            } catch (e) {
              /* metadata not ready yet — handled by the listener below */
            }
          }
          if (v.readyState >= 1) {
            apply();
          } else {
            v.addEventListener('loadedmetadata', apply, { once: true });
          }
          self.play();
          return true;
        }
      }
    );
  }

  function findFilm(from) {
    var selector = from && from.getAttribute && from.getAttribute('data-qp-film-target');
    var film = selector ? document.querySelector(selector) : null;
    if (!film) film = document.querySelector('qp-film-band[data-has-video="true"]');
    return film && typeof film.seekTo === 'function' ? film : null;
  }

  /* ── 2 · Process tiles ─────────────────────────────────────────────── */

  function markProcessLinks() {
    var linked = !!document.querySelector('qp-film-band[data-has-video="true"]');
    document.querySelectorAll('[data-qp-about-process]').forEach(function (root) {
      root.setAttribute('data-qp-film-linked', linked ? 'true' : 'false');
      root.querySelectorAll('[data-qp-film-seek]').forEach(function (tile) {
        // No film: the tile cannot do anything, so it is disabled. With a
        // film: unhide "Play film from m:ss" so the accessible name says so.
        tile.disabled = !linked;
        var label = tile.querySelector('[data-qp-film-seek-label]');
        if (label) label.toggleAttribute('hidden', !linked);
      });
    });
  }

  document.addEventListener('click', function (event) {
    var tile = event.target && event.target.closest ? event.target.closest('[data-qp-film-seek]') : null;
    if (!tile) return;
    var film = findFilm(tile);
    if (!film) return;
    event.preventDefault();
    film.seekTo(tile.getAttribute('data-qp-film-seek'));
    film.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
  });

  /* ── 3 · Support rail ───────────────────────────────────────────────── */

  function initSupport(root) {
    if (!root || root.__qpSupportInit) return;
    root.__qpSupportInit = true;

    var rail = root.querySelector('[data-qp-support-rail]');
    var links = rail ? Array.prototype.slice.call(rail.querySelectorAll('[data-qp-support-link]')) : [];
    var targets = Array.prototype.slice.call(root.querySelectorAll('[data-qp-support-target]'));
    if (!rail || !links.length || !targets.length) return;

    var byId = {};
    links.forEach(function (link) {
      var href = link.getAttribute('href') || '';
      var id = href.charAt(0) === '#' ? href.slice(1) : '';
      if (id) byId[id] = link;
    });

    var current = null;

    function keepChipInView(link) {
      var list = rail.querySelector('.qp-about-support__rail-list') || rail;
      if (getComputedStyle(list).overflowX !== 'auto' && getComputedStyle(list).overflowX !== 'scroll') return;
      var left = link.offsetLeft - list.clientWidth / 2 + link.offsetWidth / 2;
      if (typeof list.scrollTo === 'function') {
        list.scrollTo({ left: left, behavior: reducedMotion ? 'auto' : 'smooth' });
      } else {
        list.scrollLeft = left;
      }
    }

    function setActive(id) {
      if (id === current) return;
      current = id;
      links.forEach(function (link) {
        if (byId[id] === link) {
          link.setAttribute('aria-current', 'true');
          keepChipInView(link);
        } else {
          link.removeAttribute('aria-current');
        }
      });
    }

    var mobileRail = window.matchMedia ? window.matchMedia('(max-width: 767px)') : null;
    function railHeight() {
      // Only the <768px segmented control sits ABOVE the content; the
      // desktop rail is a side column and must not move the activation line.
      if (!mobileRail || !mobileRail.matches) return 0;
      return getComputedStyle(rail).position === 'sticky' ? rail.offsetHeight : 0;
    }

    function update() {
      var line = headerOffset() + railHeight() + 16;
      var activeId = targets[0].id;
      for (var i = 0; i < targets.length; i++) {
        if (targets[i].getBoundingClientRect().top <= line) activeId = targets[i].id;
      }
      // Bottom of page: the last target may never reach the line.
      var doc = document.documentElement;
      if (window.innerHeight + window.scrollY >= doc.scrollHeight - 2) {
        activeId = targets[targets.length - 1].id;
      }
      setActive(activeId);
    }

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        update();
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    root.__qpSupportTeardown = function () {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      root.__qpSupportInit = false;
      root.__qpSupportTeardown = null;
    };
    update();
  }

  function teardownSupport(root) {
    if (root && typeof root.__qpSupportTeardown === 'function') root.__qpSupportTeardown();
  }

  function openHashRow() {
    if (!location.hash || location.hash.length < 2) return;
    var id;
    try {
      id = decodeURIComponent(location.hash.slice(1));
    } catch (e) {
      return;
    }
    var el = document.getElementById(id);
    if (!el) return;
    var row = el.closest ? el.closest('details[data-qp-faq-row]') : null;
    if (row && !row.open) {
      row.open = true;
      row.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
    }
  }

  function initAll() {
    markProcessLinks();
    document.querySelectorAll('[data-qp-about-support]').forEach(initSupport);
    openHashRow();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  window.addEventListener('hashchange', openHashRow);

  document.addEventListener('shopify:section:load', function (event) {
    var target = event.target;
    if (!target || !target.querySelector) return;
    var support = target.querySelector('[data-qp-about-support]');
    if (support) {
      teardownSupport(support);
      initSupport(support);
    }
    markProcessLinks();
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var target = event.target;
    if (!target || !target.querySelector) return;
    teardownSupport(target.querySelector('[data-qp-about-support]'));
  });
})();
